import type { EnhancedPage } from '../types/navigation';

export type SpreadPageRange = {
  from: number;
  to: number;
};

export type PageRangeData = {
  from?: number;
  to: number;
  total?: number;
  chapter?: string;
  isPercentage?: boolean;
  isSegment?: boolean;
  isTimeBasedSeeking?: boolean;
};

export type AudioOnlyCalculatorParams = {
  displayIndex: number;
  pageNum: number | null;
  totalPages: number;
  totalPlaylistItems: number;
  chapter: string;
  isTimeBasedSeeking: boolean;
};

export type ScrubbingCalculatorParams = {
  pageNum: number;
  totalPageCount: number;
  chapter: string;
};

export type ContinuousCalculatorParams = {
  currentPositionPct: number;
};

export type FallbackCalculatorParams = {
  active: number;
  isReflowable: boolean;
  totalSpineItems: number;
};

export type StandardCalculatorParams = {
  isReflowable: boolean;
  isSpread: boolean;
  isContinuous: boolean;
  currentPageIndex: number;
  pages: EnhancedPage[];
  totalPageCount: number;
  isItemShownOnPage: (page: EnhancedPage) => boolean;
  getChapterTitleForPageIndex: (index: number) => string;
};

/**
 * Calculates page range for audio-only mode
 */
export const calculateAudioOnlyPageRange = (params: AudioOnlyCalculatorParams): PageRangeData => {
  const { displayIndex, pageNum, totalPages, totalPlaylistItems, chapter } = params;

  if (params.isTimeBasedSeeking) {
    return {
      to: displayIndex + 1,
      isTimeBasedSeeking: true,
    };
  }

  if (pageNum !== null) {
    return {
      to: pageNum,
      total: totalPages,
      chapter,
    };
  }

  // Return segment-based data
  return {
    to: displayIndex + 1,
    total: totalPlaylistItems,
    chapter,
    isSegment: true,
  };
};

/**
 * Calculates page range for scrubbing state with page list
 */
export const calculateScrubbingPageRange = (params: ScrubbingCalculatorParams): PageRangeData => {
  const { pageNum, totalPageCount, chapter } = params;

  return {
    to: pageNum,
    total: totalPageCount,
    chapter,
  };
};

/**
 * Calculates the page range for a spread in fixed layout mode.
 * Convention: EVEN page on the left, ODD page on the right (pages 2-3, 4-5, etc.)
 */
export const calculateSpreadPageRange = (currentPageIndex: number, pages: EnhancedPage[]): SpreadPageRange => {
  const currentPage = pages[currentPageIndex];
  const currentPageNum = currentPage?.page || 1;

  let from: number;
  let to: number;

  // Determine if we're on an even or odd page
  if (currentPageNum % 2 === 0) {
    // Even page (left side) - show current even + next odd
    from = currentPageNum;
    to = pages[currentPageIndex + 1]?.page || currentPageNum;
  } else {
    // Odd page (right side) - show previous even + current odd
    if (currentPageIndex > 0) {
      from = pages[currentPageIndex - 1]?.page || currentPageNum;
      to = currentPageNum;
    } else {
      // Edge case: the first page is odd
      from = currentPageNum;
      to = pages[currentPageIndex + 1]?.page || currentPageNum;
    }
  }

  return { from, to };
};

/**
 * Calculates page range for fallback display (no page list)
 */
export const calculateFallbackPageRange = (params: FallbackCalculatorParams): PageRangeData => {
  const { active, isReflowable, totalSpineItems } = params;

  if (isReflowable) {
    const rounded = Math.round(active);
    return {
      to: rounded,
      total: 100,
      chapter: '',
      isPercentage: true,
    };
  }

  const total = totalSpineItems || 0;
  const current = Math.floor(active) + 1;

  return {
    to: current,
    total,
    chapter: '',
  };
};

export type IsActiveParams = {
  itemPage: number;
  currentPage: number | null;
  isSpread: boolean;
  pages: EnhancedPage[];
  currentPageIndex: number;
  isItemShownOnPage: (page: EnhancedPage) => boolean;
};

const isPageInActiveSpread = (pageNum: number, currentPageIndex: number, pages: EnhancedPage[]): boolean => {
  const { from, to } = calculateSpreadPageRange(currentPageIndex, pages);
  return pageNum >= from && pageNum <= to;
};

/**
 * Check if a page item is active.
 * In spread mode, both pages in the spread should be highlighted.
 */
export const isPageActive = (params: IsActiveParams): boolean => {
  const { itemPage, currentPage, isSpread, pages, currentPageIndex, isItemShownOnPage } = params;

  // Use currentPage from position tracking (works for both audio and visual position)
  if (currentPage !== null && currentPage === itemPage) {
    return true;
  }

  // Check if this page is within the active spread
  if (isSpread && pages.length > 0 && currentPageIndex >= 0) {
    if (isPageInActiveSpread(itemPage, currentPageIndex, pages)) {
      return true;
    }
  }

  // Fallback: Check by page index
  const pageIndex = pages.findIndex((p) => p.page === itemPage);
  if (pageIndex !== -1 && pageIndex === currentPageIndex) {
    return true;
  }

  // Final fallback: visual viewport check
  const item = pages.find((p) => p.page === itemPage);
  return item ? isItemShownOnPage(item) : false;
};

/**
 * Calculates page range for standard view (reflowable/fixed with a page list)
 */
export const calculateStandardPageRange = (params: StandardCalculatorParams): PageRangeData => {
  const {
    isReflowable,
    isSpread,
    isContinuous,
    currentPageIndex,
    pages,
    totalPageCount,
    isItemShownOnPage,
    getChapterTitleForPageIndex,
  } = params;

  let from: number | undefined;
  let to: number;
  const currentPage = pages[currentPageIndex]?.page;

  if (!isReflowable && !isContinuous && isSpread) {
    // Fixed Layout Spread (two pages showing)
    const spreadRange = calculateSpreadPageRange(currentPageIndex, pages);
    from = spreadRange.from;
    to = spreadRange.to;
  } else if (!isReflowable && !isContinuous) {
    // Fixed Layout Single Page
    to = currentPage || 1;
  } else {
    // Reflowable and Continuous Visibility Logic
    const visible = pages.filter((p) =>
      isPageActive({ itemPage: p.page, currentPage, isSpread, pages, currentPageIndex, isItemShownOnPage }),
    );
    if (visible.length) {
      from = visible[0].page;
      to = visible[visible.length - 1].page;
      if (from === to) {
        from = undefined;
      }
    } else {
      to = currentPage || 1;
    }
  }

  return {
    from,
    to,
    total: totalPageCount,
    chapter: isContinuous ? undefined : getChapterTitleForPageIndex(currentPageIndex),
  };
};
